import React, { useState, useEffect, useRef } from "react";
import TopBar from "../components/karaoke/TopBar";
import SearchPanel from "../components/karaoke/SearchPanel";
import PlayerPanel from "../components/karaoke/PlayerPanel";
import QueuePanel from "../components/karaoke/QueuePanel";
import BottomControls from "../components/karaoke/BottomControls";
import AddToQueueDialog from "../components/karaoke/AddToQueueDialog";
import { SavePlaylistDialog, PlaylistsDialog } from "../components/karaoke/PlaylistDialogs";
import PendingSubmissionsDialog from "../components/karaoke/PendingSubmissionsDialog";
import QuotaStatsDialog from "../components/karaoke/QuotaStatsDialog";
import LibraryDialog from "../components/karaoke/LibraryDialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { useKaraoke } from "../store/KaraokeStore";
import { approveSubmission, deleteSubmission } from "../lib/api";
import { LabelChips } from "../components/karaoke/LabelChips";
import { classifyVersionLabels } from "../lib/labels";
import { Search, Play } from "lucide-react";
import { toast } from "sonner";

const Console = () => {
  const { addToQueue, playNow, currentSong, pendingSubmissions, searchRequestContext, clearSearchRequestContext, refreshSubmissions, recoveryContext, recoveryAlternative, recoveryPrompt, startRecoverySearch, clearRecoveryContext } = useKaraoke();
  const [pickedSong, setPickedSong] = useState(null);
  const [addMode, setAddMode] = useState("queue"); // "queue" | "play_now"
  const [openAdd, setOpenAdd] = useState(false);
  const [confirmPlayNow, setConfirmPlayNow] = useState(false);
  const [pendingPlayNow, setPendingPlayNow] = useState(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const [openSave, setOpenSave] = useState(false);
  const [openPlaylists, setOpenPlaylists] = useState(false);
  const [openPending, setOpenPending] = useState(false);
  const [openStats, setOpenStats] = useState(false);
  const [openLibrary, setOpenLibrary] = useState(false);
  const knownIds = useRef(null);

  // Toast on newly-arrived submissions
  useEffect(() => {
    const ids = new Set(pendingSubmissions.map((s) => s.id));
    if (knownIds.current === null) {
      knownIds.current = ids;
      return;
    }
    const newOnes = pendingSubmissions.filter((s) => !knownIds.current.has(s.id));
    newOnes.forEach((s) => {
      toast.success(`Nueva solicitud de ${s.singer}`, {
        description: s.title.substring(0, 50),
        action: { label: "Ver", onClick: () => setOpenPending(true) },
      });
    });
    knownIds.current = ids;
  }, [pendingSubmissions]);

  // Recovery/replacement flow: if there's a local alternative show the prompt,
  // otherwise go straight to the forced search (P2).
  useEffect(() => {
    if (recoveryPrompt > 0) {
      if (recoveryAlternative) setShowRecovery(true);
      else startRecoverySearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveryPrompt]);

  const handleUseAlternative = () => {
    if (!recoveryAlternative) return;
    const singer = recoveryContext?.singer || "Invitado";
    const dedication = recoveryContext?.dedication || "";
    const reqId = recoveryContext?.requestId || null;
    const ok = playNow(recoveryAlternative, singer, dedication);
    if (!ok) return;
    toast.success(`▶ Reproduciendo: "${(recoveryAlternative.title || "").substring(0, 40)}..."`, {
      description: `Cantará: ${singer}`,
    });
    if (reqId) {
      (async () => {
        try { await approveSubmission(reqId); await deleteSubmission(reqId); refreshSubmissions(); } catch (e) { /* keep pending */ }
      })();
    }
    setShowRecovery(false);
  };

  const handlePick = (song) => {
    setAddMode("queue");
    setPickedSong(song);
    setOpenAdd(true);
  };

  // "Reproducir ahora" from search results
  const handlePlayNow = (song) => {
    setAddMode("play_now");
    setPickedSong(song);
    setOpenAdd(true);
  };

  // Add the BEST cached version of a song to the queue: reuse the SAME modal + addToQueue call
  const handleAddBestVersion = (best, warning) => {
    if (warning) {
      toast.warning("Esta canción solo tiene versiones marcadas como inválidas", {
        description: "Considera elegir otra versión desde el buscador.",
      });
    }
    setOpenStats(false); // close stats/cached dialogs so the add modal takes focus
    setAddMode("queue");
    setPickedSong(best);
    setOpenAdd(true);
  };

  // "Reproducir ahora" using the best cached version
  const handlePlayNowBest = (best, warning) => {
    if (warning) {
      toast.warning("Esta canción solo tiene versiones marcadas como inválidas", {
        description: "Considera elegir otra versión desde el buscador.",
      });
    }
    setOpenStats(false);
    setAddMode("play_now");
    setPickedSong(best);
    setOpenAdd(true);
  };

  // Resolve a guest "search request" once a real song has been queued/played
  const resolveSearchRequest = () => {
    if (searchRequestContext?.requestId) {
      const reqId = searchRequestContext.requestId;
      (async () => {
        try {
          await approveSubmission(reqId);
          await deleteSubmission(reqId);
          refreshSubmissions();
        } catch (e) {
          /* keep pending on failure */
        }
      })();
      clearSearchRequestContext();
    }
  };

  const doPlayNow = (singer, dedication) => {
    const ok = playNow(pickedSong, singer, dedication);
    if (!ok) {
      setPickedSong(null);
      return; // invalid videoId → nothing else changes
    }
    toast.success(`▶ Reproduciendo ahora: "${(pickedSong.title || "").substring(0, 40)}..."`, {
      description: `Cantará: ${singer}${dedication ? ` · "${dedication}"` : ""}`,
    });
    resolveSearchRequest();
    setPickedSong(null);
  };

  const handleAddConfirm = (singer, dedication) => {
    if (addMode === "play_now") {
      // Ask for confirmation only when a song is currently active
      if (currentSong) {
        setPendingPlayNow({ singer, dedication });
        setConfirmPlayNow(true);
        return;
      }
      doPlayNow(singer, dedication);
      return;
    }
    const ok = addToQueue(pickedSong, singer, dedication);
    if (!ok) return; // blocked (invalid videoId)
    toast.success(`"${pickedSong.title.substring(0, 40)}..." añadida a la cola`, {
      description: `Cantará: ${singer}${dedication ? ` · "${dedication}"` : ""}`,
    });
    resolveSearchRequest();
    setPickedSong(null);
  };

  return (
    <div className="h-screen w-full flex flex-col bg-background overflow-hidden text-foreground">
      <TopBar
        onOpenPlaylists={() => setOpenPlaylists(true)}
        onSavePlaylist={() => setOpenSave(true)}
        onOpenPending={() => setOpenPending(true)}
        onOpenStats={() => setOpenStats(true)}
        onOpenLibrary={() => setOpenLibrary(true)}
      />
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-4 p-4 overflow-hidden">
        <SearchPanel onPick={handlePick} onPlayNow={handlePlayNow} />
        <PlayerPanel />
        <QueuePanel />
      </main>
      <BottomControls />

      <AddToQueueDialog
        open={openAdd}
        onOpenChange={setOpenAdd}
        song={pickedSong}
        onConfirm={handleAddConfirm}
        defaultSinger={searchRequestContext?.singer}
        defaultDedication={searchRequestContext?.dedication}
        mode={addMode}
      />
      <SavePlaylistDialog open={openSave} onOpenChange={setOpenSave} />
      <PlaylistsDialog open={openPlaylists} onOpenChange={setOpenPlaylists} />
      <PendingSubmissionsDialog open={openPending} onOpenChange={setOpenPending} />
      <QuotaStatsDialog open={openStats} onOpenChange={setOpenStats} onAddSong={handleAddBestVersion} onPlayNow={handlePlayNowBest} />
      <LibraryDialog open={openLibrary} onOpenChange={setOpenLibrary} />

      <AlertDialog open={confirmPlayNow} onOpenChange={setConfirmPlayNow}>
        <AlertDialogContent className="bg-surface border-border" data-testid="play-now-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">¿Reproducir ahora?</AlertDialogTitle>
            <AlertDialogDescription>
              Esto detendrá la canción actual. ¿Deseas reproducir esta canción ahora?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="play-now-cancel-button"
              onClick={() => { setPendingPlayNow(null); setPickedSong(null); }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="play-now-confirm-button"
              onClick={() => {
                if (pendingPlayNow) doPlayNow(pendingPlayNow.singer, pendingPlayNow.dedication);
                setPendingPlayNow(null);
              }}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
            >
              Reproducir ahora
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showRecovery} onOpenChange={setShowRecovery}>
        <AlertDialogContent className="bg-surface border-border" data-testid="recovery-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">
              {recoveryContext?.reason === "manual_replace" ? "Reemplazar versión" : "No se pudo reproducir esta versión"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p className="text-muted-foreground">
                  Cantará: <span className="text-secondary font-semibold">{recoveryContext?.singer || "Invitado"}</span>
                </p>
                <p className="text-foreground font-medium">Encontramos otra versión disponible:</p>
                {recoveryAlternative && (
                  <div className="flex gap-3 p-2.5 rounded-md bg-background border border-border" data-testid="recovery-alternative-card">
                    <div className="relative shrink-0">
                      <img src={recoveryAlternative.thumbnail} alt="" className="w-24 h-14 object-cover rounded-sm bg-muted" />
                      {recoveryAlternative.duration && (
                        <span className="absolute bottom-0.5 right-0.5 bg-black/85 text-white text-[10px] font-mono px-1 rounded-sm">
                          {recoveryAlternative.duration}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold line-clamp-2 leading-tight text-foreground">{recoveryAlternative.title}</p>
                      <LabelChips labels={recoveryAlternative.labels || classifyVersionLabels(`${recoveryAlternative.title} ${recoveryAlternative.channelTitle}`)} content={recoveryAlternative.content_labels} className="mt-1" />
                      <p className="text-xs text-muted-foreground mt-1 truncate">{recoveryAlternative.channelTitle}</p>
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel data-testid="recovery-cancel-button" className="mt-0">
              Cancelar
            </AlertDialogCancel>
            <button
              data-testid="recovery-search-button"
              onClick={() => { startRecoverySearch(); setShowRecovery(false); }}
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md border border-border hover:border-secondary hover:text-secondary font-bold uppercase tracking-wide text-xs transition-colors"
            >
              <Search className="w-4 h-4" /> Buscar otras versiones
            </button>
            <AlertDialogAction
              data-testid="recovery-use-alternative-button"
              onClick={handleUseAlternative}
              className="bg-gav-primary hover:bg-transparent gap-2"
            >
              <Play className="w-4 h-4" fill="currentColor" /> Usar esta versión
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Console;
